import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Code2,
  Cpu,
  Server,
  ShieldCheck,
  Database,
  UserCheck,
  Briefcase,
  ChevronDownCircle,
} from "lucide-react";
import TiltCard from "./TiltCard";
import { Link } from "react-router-dom";

export default function ASPNetPage() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    window.scrollTo(0, 0);
  }, []);

  const curriculum = [
    "Introduction to ASP.NET & .NET Core",
    "C# Programming Basics & OOP",
    "Entity Framework & LINQ",
    "Web Forms, MVC & Razor Pages",
    "REST API Development",
    "Authentication & Authorization",
    "Deployment to IIS & Azure",
    "Hands-on Real-time Project",
  ];

  const careers = [
    "Web Developer",
    "Full Stack .NET Developer",
    "Backend API Developer",
    "Cloud Developer with Azure",
    "Software Engineer",
  ];

  const faqs = [
    {
      question: "Do I need prior programming experience?",
      answer: "No. We start from the basics of programming with C# and .NET.",
    },
    {
      question: "Will I get a certificate after completion?",
      answer:
        "Yes. You will receive an industry-recognized certificate from our institute.",
    },
    {
      question: "Are there placement opportunities?",
      answer:
        "Yes. We offer placement support and have tie-ups with local IT companies.",
    },
  ];

  const benefits = [
    { icon: Code2, text: "Powerful backend logic with C# and Visual Basic" },
    { icon: Server, text: "Database and API integration" },
    { icon: ShieldCheck, text: "Enterprise-level security features" },
    { icon: Cpu, text: "High performance with .NET Core" },
    { icon: Database, text: "Works with SQL Server, MySQL, and more" },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero with Background Image */}
      <section
        className="relative text-white py-28 text-center"
        data-aos="fade-down"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://i.pinimg.com/736x/1c/d4/ef/1cd4efd8507ac3302415fe5a3604ae6e.jpg')",
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 mt-10">
            ASP.NET Software Development
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8">
            Learn to build secure, scalable, and enterprise-grade web
            applications with ASP.NET.
          </p>

          {/* Enroll Button */}
          <Link
            to="/contact"
            className="inline-block bg-yellow-400 text-green-800 font-semibold px-6 py-2 rounded-md shadow-lg transform transition duration-300 hover:scale-105 hover:bg-yellow-300"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              "url('https://wallpapercave.com/wp/wp6606785.jpg')",
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          {/* Image */}
          <div data-aos="fade-right">
            <img
              src="https://i.pinimg.com/1200x/9e/bd/cc/9ebdcc8215bbf83c420b157a231aead0.jpg"
              alt="ASP.NET"
              className="rounded-2xl shadow-lg w-full object-cover"
            />
          </div>
          {/* Text */}
          <div data-aos="fade-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Learn ASP.NET?
            </h2>
            <ul className="space-y-4">
              {benefits.map((item, index) => (
                <TiltCard key={index}>
                  <li
                    className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-lg transform transition-transform duration-500 hover:scale-105 hover:shadow-2xl"
                    data-aos="zoom-in"
                    data-aos-delay={index * 150}
                  >
                    {/* Icon with animation */}
                    <item.icon
                      className="text-green-700 mt-1 animate-bounce-slow"
                      size={24}
                    />
                    <span className="text-gray-900 font-medium">
                      {item.text}
                    </span>
                  </li>
                </TiltCard>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              "url('https://wallpaperaccess.com/full/2825711.jpg')",
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-12 text-gray-900"
            data-aos="fade-up"
          >
            What You’ll Learn
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {curriculum.map((topic, idx) => (
              <TiltCard key={idx}>
                <div
                  data-aos="zoom-in-up"
                  className="bg-green-50 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform"
                >
                  <h5 className="text-lg font-semibold text-gray-900">
                    {topic}
                  </h5>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* Syllabus Section */}
      <section className="py-20 bg-white" id="syllabus">
        <div className="max-w-6xl mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-12 text-gray-900"
            data-aos="fade-up"
          >
            Course Syllabus
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                module: "Module 1: ASP.NET & C# Basics",
                topics: [
                  "Introduction to ASP.NET & .NET Core",
                  "C# Programming Basics",
                  "OOP Concepts",
                ],
                color: "bg-indigo-100 text-indigo-700",
                icon: Code2,
              },
              {
                module: "Module 2: Web Development & MVC",
                topics: [
                  "Web Forms & Razor Pages",
                  "MVC Architecture",
                  "Routing & Views",
                ],
                color: "bg-green-100 text-green-700",
                icon: Server,
              },
              {
                module: "Module 3: Database & APIs",
                topics: [
                  "Entity Framework & LINQ",
                  "REST API Development",
                  "SQL Server & MySQL Integration",
                ],
                color: "bg-yellow-100 text-yellow-700",
                icon: Database,
              },
              {
                module: "Module 4: Security & Auth",
                topics: [
                  "Authentication & Authorization",
                  "Data Validation & Security Best Practices",
                ],
                color: "bg-red-100 text-red-700",
                icon: ShieldCheck,
              },
              {
                module: "Module 5: Deployment & Project",
                topics: [
                  "Deployment to IIS & Azure",
                  "Hands-on Real-time Project",
                ],
                color: "bg-purple-100 text-purple-700",
                icon: Cpu,
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className={`rounded-2xl p-6 shadow-lg ${card.color} transition transform hover:scale-105 hover:shadow-2xl`}
                data-aos="zoom-in"
                data-aos-delay={idx * 150}
              >
                <div className="flex items-center justify-center mb-4">
                  <card.icon className="h-10 w-10 text-current animate-bounce" />
                </div>
                <h3 className="text-xl font-bold mb-3">{card.module}</h3>
                <ul className="list-disc list-inside text-gray-900 space-y-1">
                  {card.topics.map((topic, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2"
                      data-aos="fade-right"
                      data-aos-delay={i * 100}
                    >
                      <ChevronDownCircle className="h-5 w-5 text-current animate-pulse" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-time Project */}
      {/* <section className="py-20 relative text-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              "url('https://wallpapercave.com/wp/wp2555734.jpg')",
          }}
        ></div>
        <div className="relative max-w-4xl mx-auto" data-aos="fade-up">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Hands-on Real-Time Project
          </h2>
          <p className="text-gray-700 mb-8">
            Build a live e-commerce web application with secure login, product
            management, and payment integration.
          </p>
          <img
            src="/real-project.svg"
            alt="Project Demo"
            className="mx-auto rounded shadow w-4/5"
          />
        </div>
      </section> */}

      {/* Careers */}
      <section className="py-20 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              "url('https://wallpapercave.com/wp/wp6606787.jpg')",
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-12 text-gray-900"
            data-aos="fade-up"
          >
            Career Opportunities
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {careers.map((career, idx) => (
              <TiltCard key={idx}>
                <div
                  data-aos="zoom-in"
                  className="bg-green-50 rounded-2xl shadow-lg p-6 hover:scale-105 transition-transform h-full flex flex-col items-center justify-center"
                >
                  <Briefcase className="text-green-700 mb-3" size={32} />
                  <h5 className="font-semibold text-gray-900">{career}</h5>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section className="py-20 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              "url('https://wallpaper.dog/large/20439322.jpg')",
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto text-center">
          <h2
            className="text-3xl font-bold mb-12 text-gray-900"
            data-aos="fade-up"
          >
            Student Success Stories
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {["Siva", "Ramya", "Karthik"].map((name, idx) => (
              <TiltCard key={idx}>
                <div
                  data-aos="fade-up"
                  data-aos-delay={idx * 200}
                  className="bg-white rounded-2xl shadow-lg p-6 hover:scale-105 transition-transform h-full"
                >
                  <UserCheck className="text-green-700 mb-3 mx-auto" size={36} />
                  <h5 className="font-semibold text-gray-900 mb-2">{name}</h5>
                  <p className="text-gray-700 text-sm">
                    “After this course, I landed my first job as a .NET Developer
                    at a software firm in Chennai!”
                  </p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQs */}
      <section className="py-20 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              "url('https://bairesdev.mo.cloudinary.net/blog/2023/06/asp-net-core-identity-with-patterns.jpg?tx=w_1920,q_auto')",
          }}
        ></div>
        <div className="relative max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            FAQs
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <TiltCard key={idx}>
                <div
                  data-aos="fade-up"
                  className="border border-green-100 rounded-xl overflow-hidden bg-white shadow-lg"
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 text-left text-gray-900 font-semibold focus:outline-none hover:bg-green-50 transition-colors"
                    onClick={() => {
                      const el = document.getElementById(`faq-${idx}`);
                      el.classList.toggle("hidden");
                    }}
                  >
                    <span>{faq.question}</span>
                    <ChevronDownCircle className="text-green-700" size={20} />
                  </button>
                  <div
                    id={`faq-${idx}`}
                    className="px-6 py-4 text-gray-700 hidden"
                  >
                    {faq.answer}
                  </div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 relative text-white text-center"
        data-aos="zoom-in"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-green-900 bg-opacity-70"></div>
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-4">Ready to Start?</h2>
          <p className="mb-6 text-lg">
            Enroll now and become a certified ASP.NET Developer.
          </p>
          <Link
            to="/contact"
            className="bg-yellow-400 text-green-800 font-semibold px-6 py-2 rounded-md hover:bg-yellow-300 transition-colors inline-block"
          >
            Enroll Now
          </Link>
        </div>
      </section>
    </div>
  );
}
